import { useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  Title,
  Tooltip,
  Legend,
  ScatterController,
  Filler,
  CategoryScale,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";

ChartJS.register(
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  ScatterController,
  Filler
);

const isFiniteNum = (v) => Number.isFinite(Number(v));

function clamp(v, a, b) {
  return Math.min(Math.max(v, a), b);
}

function getNestedPitchPayload(assessment) {
  return (
    assessment?.intonation?.intensity ||
    assessment?.intonation ||
    assessment?.intensity ||
    null
  );
}

function hasPitchData(intensity) {
  const xs = intensity?.pitch?.xs;
  const ys = intensity?.pitch?.ys;

  return Array.isArray(xs) && Array.isArray(ys) && xs.length > 1 && ys.length > 1;
}

function buildContinuousGuideCurve(xs, ys) {
  if (!xs?.length || !ys?.length) return [];

  const out = new Array(ys.length).fill(null);

  for (let i = 0; i < ys.length; i++) {
    const y = ys[i];
    out[i] = y != null && Number.isFinite(Number(y)) ? Number(y) : null;
  }

  let firstValid = -1;

  for (let i = 0; i < out.length; i++) {
    if (out[i] != null) {
      firstValid = i;
      break;
    }
  }

  if (firstValid === -1) return [];

  for (let i = 0; i < firstValid; i++) {
    out[i] = out[firstValid];
  }

  let lastValid = -1;

  for (let i = out.length - 1; i >= 0; i--) {
    if (out[i] != null) {
      lastValid = i;
      break;
    }
  }

  for (let i = lastValid + 1; i < out.length; i++) {
    out[i] = out[lastValid];
  }

  let i = firstValid;

  while (i < out.length) {
    if (out[i] != null) {
      i++;
      continue;
    }

    const start = i - 1;
    let end = i;

    while (end < out.length && out[end] == null) {
      end++;
    }

    if (end >= out.length) break;

    const x1 = Number(xs[start]);
    const x2 = Number(xs[end]);
    const y1 = out[start];
    const y2 = out[end];
    const span = x2 - x1 || 1;

    for (let k = i; k < end; k++) {
      const x = Number(xs[k]);
      const t = (x - x1) / span;
      out[k] = y1 + (y2 - y1) * t;
    }

    i = end;
  }

  const smoothPass = (arr) => {
    const next = [...arr];

    for (let j = 1; j < arr.length - 1; j++) {
      next[j] = (arr[j - 1] + arr[j] + arr[j + 1]) / 3;
    }

    return next;
  };

  let smoothed = out.map((v) => Number(v));
  smoothed = smoothPass(smoothed);
  smoothed = smoothPass(smoothed);

  return xs.map((x, idx) => ({
    x: Number(x),
    y: smoothed[idx],
  }));
}

function interpolateOnCurve(points, xTarget) {
  if (!points?.length || !Number.isFinite(Number(xTarget))) return null;

  if (points.length === 1) {
    return {
      x: xTarget,
      y: points[0].y,
    };
  }

  if (xTarget <= points[0].x) {
    return {
      x: xTarget,
      y: points[0].y,
    };
  }

  if (xTarget >= points[points.length - 1].x) {
    return {
      x: xTarget,
      y: points[points.length - 1].y,
    };
  }

  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];

    const minX = Math.min(p1.x, p2.x);
    const maxX = Math.max(p1.x, p2.x);

    if (xTarget >= minX && xTarget <= maxX) {
      const span = p2.x - p1.x || 1;
      const t = (xTarget - p1.x) / span;

      return {
        x: xTarget,
        y: p1.y + (p2.y - p1.y) * t,
      };
    }
  }

  return {
    x: xTarget,
    y: points[points.length - 1].y,
  };
}

function makeCursorBallPlugin() {
  return {
    id: "cursorBallPlugin",

    afterDatasetsDraw(chart, _args, pluginOptions) {
      const {
        cursorX = null,
        cursorEnvelopePoint = null,
        ballRadius = 12,
        showBall = true,
        isMobile = false,
      } = pluginOptions || {};

      if (!showBall || cursorX == null) return;

      const { ctx, chartArea, scales } = chart;
      const xScale = scales?.x;
      const yScale = scales?.y;

      if (!ctx || !chartArea || !xScale || !yScale) return;

      const { left, right, top, bottom } = chartArea;

      const xC = xScale.getPixelForValue(cursorX);

      if (Number.isFinite(xC) && xC >= left && xC <= right) {
        ctx.save();

        ctx.strokeStyle = "rgba(255,255,255,0.95)";
        ctx.lineWidth = isMobile ? 3 : 3.5;
        ctx.beginPath();
        ctx.moveTo(xC, top);
        ctx.lineTo(xC, bottom);
        ctx.stroke();

        ctx.strokeStyle = "rgba(16,185,129,1)";
        ctx.lineWidth = isMobile ? 1.6 : 1.8;
        ctx.beginPath();
        ctx.moveTo(xC, top);
        ctx.lineTo(xC, bottom);
        ctx.stroke();

        ctx.restore();
      }

      if (
        !cursorEnvelopePoint ||
        !Number.isFinite(Number(cursorEnvelopePoint.x)) ||
        !Number.isFinite(Number(cursorEnvelopePoint.y))
      ) {
        return;
      }

      const r = ballRadius ?? (isMobile ? 8.5 : 12);
      const px = xScale.getPixelForValue(cursorEnvelopePoint.x);
      const py = yScale.getPixelForValue(cursorEnvelopePoint.y);

      if (!Number.isFinite(px) || !Number.isFinite(py)) return;
      if (px < left || px > right || py < top || py > bottom) return;

      ctx.save();

      ctx.shadowColor = "rgba(16,185,129,0.48)";
      ctx.shadowBlur = isMobile ? 14 : 18;

      ctx.beginPath();
      ctx.arc(px, py, r + 1.25, 0, Math.PI * 2);
      ctx.fillStyle = "rgba(16,185,129,0.95)";
      ctx.fill();

      ctx.shadowBlur = 0;

      const bodyGrad = ctx.createRadialGradient(
        px - r * 0.32,
        py - r * 0.34,
        r * 0.18,
        px,
        py,
        r
      );

      bodyGrad.addColorStop(0, "rgba(220,252,231,1)");
      bodyGrad.addColorStop(0.2, "rgba(134,239,172,1)");
      bodyGrad.addColorStop(0.5, "rgba(34,197,94,1)");
      bodyGrad.addColorStop(0.8, "rgba(16,185,129,1)");
      bodyGrad.addColorStop(1, "rgba(4,120,87,1)");

      ctx.beginPath();
      ctx.arc(px, py, r, 0, Math.PI * 2);
      ctx.fillStyle = bodyGrad;
      ctx.fill();

      ctx.lineWidth = 2.4;
      ctx.strokeStyle = "rgba(255,255,255,0.92)";
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(
        px - r * 0.34,
        py - r * 0.38,
        Math.max(1.8, r * 0.26),
        0,
        Math.PI * 2
      );
      ctx.fillStyle = "rgba(255,255,255,0.88)";
      ctx.fill();

      ctx.beginPath();
      ctx.arc(
        px - r * 0.08,
        py - r * 0.14,
        Math.max(1.2, r * 0.11),
        0,
        Math.PI * 2
      );
      ctx.fillStyle = "rgba(255,255,255,0.32)";
      ctx.fill();

      ctx.beginPath();
      ctx.arc(
        px + r * 0.28,
        py + r * 0.34,
        Math.max(1.4, r * 0.18),
        0,
        Math.PI * 2
      );
      ctx.fillStyle = "rgba(0,0,0,0.10)";
      ctx.fill();

      ctx.restore();
    },
  };
}

export default function PitchCurveAudioPanel({
  assessment,
  intonationOverride = null,
  audioRef,
  speed = 1,
  title = "Voice melody",
  subtitle = "Preview the voice contour, then press play to begin rating.",
  compact = false,
  expanded = true,
  onExpandedChange,
}) {
  const [isMobile, setIsMobile] = useState(false);
  const [animatedTime, setAnimatedTime] = useState(0);

  const rafRef = useRef(null);
  const displayTimeRef = useRef(0);

  const intensity = useMemo(() => {
    return intonationOverride?.intensity || getNestedPitchPayload(assessment);
  }, [assessment, intonationOverride]);

  const xs = useMemo(() => intensity?.pitch?.xs || [], [intensity]);
  const ysRaw = useMemo(() => intensity?.pitch?.ys || [], [intensity]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => {
      window.removeEventListener("resize", checkMobile);
    };
  }, []);

  useEffect(() => {
    const audio = audioRef?.current;

    if (!audio) {
      displayTimeRef.current = 0;
      setAnimatedTime(0);
      return;
    }

    const clock = {
      audioStartTime: Math.max(0, audio.currentTime || 0),
      perfStartTime: performance.now(),
      playbackRate: audio.playbackRate || speed || 1,
      running: false,
    };

    const stopRaf = () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    const syncFromAudio = () => {
      clock.audioStartTime = Math.max(0, audio.currentTime || 0);
      clock.perfStartTime = performance.now();
      clock.playbackRate = audio.playbackRate || speed || 1;

      displayTimeRef.current = clock.audioStartTime;
      setAnimatedTime(clock.audioStartTime);
    };

    const getClockTime = () => {
      if (!clock.running) {
        return Math.max(0, audio.currentTime || 0);
      }

      const elapsedSeconds =
        ((performance.now() - clock.perfStartTime) / 1000) *
        clock.playbackRate;

      return clock.audioStartTime + elapsedSeconds;
    };

    const tick = () => {
      const nextTime = getClockTime();

      displayTimeRef.current = nextTime;
      setAnimatedTime(nextTime);

      rafRef.current = requestAnimationFrame(tick);
    };

    const startClock = () => {
      syncFromAudio();
      clock.running = true;

      stopRaf();
      rafRef.current = requestAnimationFrame(tick);
    };

    const stopClockAndSync = () => {
      clock.running = false;
      syncFromAudio();
    };

    const handlePlay = () => {
      startClock();
    };

    const handlePlaying = () => {
      startClock();
    };

    const handlePause = () => {
      stopClockAndSync();
    };

    const handleSeeked = () => {
      syncFromAudio();
    };

    const handleLoadedMetadata = () => {
      syncFromAudio();
    };

    const handleRateChange = () => {
      syncFromAudio();

      if (!audio.paused && !audio.ended) {
        clock.running = true;
      }
    };

    const handleWaiting = () => {
      clock.running = false;
      syncFromAudio();
    };

    const handleEnded = () => {
      clock.running = false;
      syncFromAudio();
      stopRaf();
    };

    syncFromAudio();

    if (!audio.paused && !audio.ended) {
      clock.running = true;
      rafRef.current = requestAnimationFrame(tick);
    }

    audio.addEventListener("play", handlePlay);
    audio.addEventListener("playing", handlePlaying);
    audio.addEventListener("pause", handlePause);
    audio.addEventListener("seeked", handleSeeked);
    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ratechange", handleRateChange);
    audio.addEventListener("waiting", handleWaiting);
    audio.addEventListener("ended", handleEnded);

    return () => {
      stopRaf();

      audio.removeEventListener("play", handlePlay);
      audio.removeEventListener("playing", handlePlaying);
      audio.removeEventListener("pause", handlePause);
      audio.removeEventListener("seeked", handleSeeked);
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ratechange", handleRateChange);
      audio.removeEventListener("waiting", handleWaiting);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [audioRef, speed]);

  const ys = useMemo(() => {
    const out = new Array(ysRaw.length);

    for (let i = 0; i < ysRaw.length; i++) {
      const v = Number(ysRaw[i]);
      out[i] = isFiniteNum(v) && v > 0 ? v : null;
    }

    return out;
  }, [ysRaw]);

  const { voicedXMin, voicedXMax } = useMemo(() => {
    if (!xs.length || !ys.length) {
      return {
        voicedXMin: 0,
        voicedXMax: 1,
      };
    }

    let first = -1;
    let last = -1;

    for (let i = 0; i < ys.length; i++) {
      if (ys[i] != null) {
        first = i;
        break;
      }
    }

    for (let i = ys.length - 1; i >= 0; i--) {
      if (ys[i] != null) {
        last = i;
        break;
      }
    }

    if (first === -1 || last === -1) {
      return {
        voicedXMin: Number(xs[0] ?? 0),
        voicedXMax: Number(xs[xs.length - 1] ?? 1),
      };
    }

    return {
      voicedXMin: Number(xs[first]),
      voicedXMax: Number(xs[last]),
    };
  }, [xs, ys]);

  const { yMin, yMax } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;

    for (let i = 0; i < ys.length; i++) {
      const v = ys[i];

      if (v == null) continue;

      min = Math.min(min, v);
      max = Math.max(max, v);
    }

    if (!Number.isFinite(min) || !Number.isFinite(max)) {
      return {
        yMin: 0,
        yMax: 1,
      };
    }

    return {
      yMin: min,
      yMax: max,
    };
  }, [ys]);

  const yRange = useMemo(() => {
    return Math.max(1e-6, yMax - yMin);
  }, [yMax, yMin]);

  const cursorX = useMemo(() => {
    if (!xs.length) return null;

    const t = Number(animatedTime);
    const raw = isFiniteNum(t) ? t : voicedXMin;

    return clamp(raw, voicedXMin, voicedXMax);
  }, [animatedTime, xs.length, voicedXMin, voicedXMax]);

  const predictedCursorX = useMemo(() => {
    if (!xs.length) return null;

    const leadSeconds =
      speed <= 0.5 ? 0.045 :
      speed <= 1 ? 0.035 :
      0.025;

    return clamp((cursorX ?? voicedXMin) + leadSeconds, voicedXMin, voicedXMax);
  }, [cursorX, xs.length, voicedXMin, voicedXMax, speed]);

  const guideCurve = useMemo(() => {
    return buildContinuousGuideCurve(xs, ys);
  }, [xs, ys]);

  const cursorEnvelopePoint = useMemo(() => {
    if (!guideCurve.length || predictedCursorX == null) return null;

    const point = interpolateOnCurve(guideCurve, predictedCursorX);

    if (!point) return null;

    return {
      x: point.x,
      y: point.y,
    };
  }, [guideCurve, predictedCursorX]);

  const ballRadius = useMemo(() => {
    return isMobile ? 8.5 : 12;
  }, [isMobile]);

  const cursorBallPlugin = useMemo(() => makeCursorBallPlugin(), []);

  const data = useMemo(() => {
    return {
      datasets: [
        {
          label: "Pitch",
          data: xs.map((x, i) => ({
            x: Number(x),
            y: ys[i],
          })),
          borderColor: "#2563eb",
          backgroundColor: "rgba(37,99,235,0.08)",
          fill: true,
          borderWidth: isMobile ? 1.75 : 2.25,
          tension: isMobile ? 0.25 : 0.42,
          cubicInterpolationMode: "monotone",
          pointRadius: 0,
          showLine: true,
          spanGaps: false,
          borderCapStyle: "round",
          borderJoinStyle: "round",
          datalabels: {
            display: false,
          },
        },
      ],
    };
  }, [xs, ys, isMobile]);

  const options = useMemo(() => {
    const xPadding = (voicedXMax - voicedXMin) * 0.025;

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      layout: {
        padding: {
          left: 8,
          right: 12,
          top: 8,
          bottom: 8,
        },
      },
      scales: {
        x: {
          type: "linear",
          min: voicedXMin - xPadding,
          max: voicedXMax + xPadding,
          grid: {
            drawBorder: false,
            color: "rgba(15,23,42,0.05)",
          },
          ticks: {
            display: false,
          },
          title: {
            display: false,
          },
        },
        y: {
          min: Math.max(0, yMin - yRange * 0.12),
          max: yMax + yRange * 0.18,
          grid: {
            color: "rgba(15,23,42,0.06)",
            drawBorder: false,
          },
          title: {
            display: !compact,
            text: "Pitch",
            font: {
              size: isMobile ? 11 : 12,
              weight: "bold",
            },
          },
          ticks: {
            font: {
              size: isMobile ? 10 : 11,
            },
            maxTicksLimit: 4,
            callback: (value) => Number(value).toFixed(1),
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: !isMobile,
          mode: "nearest",
          intersect: false,
          callbacks: {
            label: (ctx) => {
              const x = Number(ctx.parsed.x).toFixed(2);
              const y = Number(ctx.parsed.y).toFixed(2);

              return `t ${x}s · pitch ${y}`;
            },
          },
        },
        datalabels: {
          display: false,
        },
        cursorBallPlugin: {
          cursorX: predictedCursorX,
          cursorEnvelopePoint,
          ballRadius,
          showBall: true,
          isMobile,
        },
      },
      interaction: {
        mode: "nearest",
        axis: "x",
        intersect: false,
      },
      elements: {
        point: {
          radius: 0,
          hoverRadius: 0,
          hitRadius: 0,
        },
      },
    };
  }, [
    compact,
    isMobile,
    voicedXMin,
    voicedXMax,
    yMin,
    yMax,
    yRange,
    predictedCursorX,
    cursorEnvelopePoint,
    ballRadius,
  ]);

  if (!hasPitchData(intensity)) {
    return null;
  }

  if (!expanded) {
    return (
      <div className="mt-4 min-w-0 rounded-[20px] border border-slate-200 bg-white/90 p-3 shadow-sm transition-all duration-500 ease-out">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-black text-slate-950">{title}</div>
            <div className="mt-0.5 truncate text-xs font-semibold text-slate-500">
              Played once. Open to inspect the voice melody again.
            </div>
          </div>

          <button
            type="button"
            onClick={() => onExpandedChange?.(true)}
            className="shrink-0 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-2 text-xs font-black text-blue-700 transition hover:bg-blue-100"
          >
            Show graph
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 min-w-0 rounded-[20px] border border-slate-200 bg-white/90 p-3 shadow-sm transition-all duration-500 ease-out">
      <div className="mb-2 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-black text-slate-950">{title}</div>

          <div className="mt-0.5 text-xs font-semibold text-slate-500">
            {subtitle}
          </div>
        </div>

        <button
          type="button"
          onClick={() => onExpandedChange?.(false)}
          className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-black text-slate-600 transition hover:bg-slate-100"
        >
          Hide graph
        </button>
      </div>

      <div
        className={`w-full min-w-0 overflow-hidden rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-2 ${
          compact || isMobile ? "h-32" : "h-40"
        }`}
      >
        <Line data={data} options={options} plugins={[cursorBallPlugin]} />
      </div>

      <div className="mt-1.5 flex justify-between text-[11px] font-bold text-slate-400">
        <span>Start</span>
        <span>Follows playback</span>
        <span>End</span>
      </div>
    </div>
  );
}